import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import {
    POSITION_STEP,
    getPlacement,
    imageTravel,
    pictureRect,
    snapPosition,
    stepScale,
    zoomPlacement,
} from '@components/shared/spreadLayoutUtils';

/** Pointer travel before a press on the page counts as a drag rather than a click. */
const DRAG_THRESHOLD = 4;

/** Wheel distance per scale step: one mouse-wheel notch, or a short trackpad swipe. */
const WHEEL_STEP = 50;

/** How long the cropped-off picture stays visible after the last zoom step. */
const ZOOM_SHOW_MS = 900;

/** The picture's own size: the given dimensions, or the <img> inside the page once loaded. */
function naturalOf(page, natural) {
    if (natural?.width && natural?.height) return natural;
    const img = page.querySelector('img');
    return img?.naturalWidth ? { width: img.naturalWidth, height: img.naturalHeight } : null;
}

function pageBox(page) {
    const rect = page.getBoundingClientRect();
    return { rect, size: { width: rect.width, height: rect.height } };
}

/**
 * Drag an image around its page, snapping to the placement grid, and zoom it by
 * scrolling over it around the mouse position. A press that does not move still
 * reaches the page's click handler.
 *
 * @param {Object} options
 * @param {{ scale?: number, fill?: boolean, anchor?: string, x?: number, y?: number }} options.image
 * @param {{ width: number, height: number }} [options.natural] - picture size, when the page has no <img>
 * @param {(placement: { x: number, y: number }) => void} [options.onPlace] - omit to disable dragging
 * @param {(scale: number, placement: { x: number, y: number }) => void} [options.onScale] - omit to leave the wheel alone
 * @returns {{ placement, guides, handlers }} live placement, snap guides while dragging, page props
 */
export default function usePlacementDrag({ image, natural, onPlace, onScale }) {
    const [live, setLive] = useState(null);
    const [zooming, setZooming] = useState(false);
    const [hovering, setHovering] = useState(false);
    const stateRef = useRef(null);
    const suppressClickRef = useRef(false);

    const scale = image?.scale || 100;
    const fill = Boolean(image?.fill);
    const settled = getPlacement(image);
    const wheelRef = useRef({ el: null, listener: null, distance: 0, pending: null, timer: null });
    const latestRef = useRef(null);
    latestRef.current = { scale, fill, placement: settled, natural, onScale };

    useEffect(() => {
        wheelRef.current.pending = null;
    }, [scale, fill, settled.x, settled.y]);

    useEffect(() => () => clearTimeout(wheelRef.current.timer), []);

    // React registers wheel listeners as passive, so scrolling would also move the page.
    const pageRef = useCallback((el) => {
        const wheel = wheelRef.current;
        if (wheel.el && wheel.listener) wheel.el.removeEventListener('wheel', wheel.listener);
        wheel.el = el;
        if (!el) return;
        wheel.listener = (event) => {
            const latest = latestRef.current;
            if (!latest.onScale) return;
            event.preventDefault();
            const lines = event.deltaMode === 1 ? 16 : 1;
            wheel.distance += event.deltaY * lines;
            if (Math.abs(wheel.distance) < WHEEL_STEP) return;
            const steps = wheel.distance < 0 ? 1 : -1;
            wheel.distance = 0;
            const from = wheel.pending ?? { scale: latest.scale, ...latest.placement };
            const toScale = stepScale(from.scale, latest.fill, steps);
            if (toScale === from.scale) return;
            const { rect, size } = pageBox(el);
            const placement = zoomPlacement({
                page: size,
                natural: naturalOf(el, latest.natural),
                fill: latest.fill,
                from,
                toScale,
                point: { x: event.clientX - rect.left, y: event.clientY - rect.top },
            });
            wheel.pending = { scale: toScale, ...placement };
            latest.onScale(toScale, placement);
            setZooming(true);
            clearTimeout(wheel.timer);
            wheel.timer = setTimeout(() => setZooming(false), ZOOM_SHOW_MS);
        };
        el.addEventListener('wheel', wheel.listener, { passive: false });
    }, []);

    const onPointerDown = useCallback((event) => {
        suppressClickRef.current = false;
        if (!onPlace || event.button !== 0) return;
        const page = event.currentTarget;
        const { size } = pageBox(page);
        const room = imageTravel(size, naturalOf(page, natural), scale, fill);
        if (!room) return;
        stateRef.current = {
            startX: event.clientX,
            startY: event.clientY,
            origin: getPlacement(image),
            travel: { x: room.x, y: room.y, width: size.width, height: size.height },
            moved: false,
        };
        try {
            page.setPointerCapture(event.pointerId);
        } catch {
            // The pointer is already gone; moves still arrive while it is over the page.
        }
    }, [image, natural, scale, fill, onPlace]);

    const onPointerMove = useCallback((event) => {
        const state = stateRef.current;
        if (!state) return;
        const dx = event.clientX - state.startX;
        const dy = event.clientY - state.startY;
        if (!state.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
        state.moved = true;
        event.preventDefault();
        const { travel, origin } = state;
        const x = Math.abs(travel.x) >= 1 ? snapPosition(origin.x + (dx / travel.x) * 100) : origin.x;
        const y = Math.abs(travel.y) >= 1 ? snapPosition(origin.y + (dy / travel.y) * 100) : origin.y;
        if (state.x === x && state.y === y) return;
        state.x = x;
        state.y = y;
        setLive({ x, y, travel });
    }, []);

    const finish = useCallback((event, commit) => {
        const state = stateRef.current;
        stateRef.current = null;
        if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }
        if (!state?.moved) return;
        suppressClickRef.current = true;
        setLive(null);
        const changed = state.x !== undefined && (state.x !== state.origin.x || state.y !== state.origin.y);
        if (commit && changed) onPlace?.({ x: state.x, y: state.y });
    }, [onPlace]);

    const onClickCapture = useCallback((event) => {
        if (!suppressClickRef.current) return;
        suppressClickRef.current = false;
        event.preventDefault();
        event.stopPropagation();
    }, []);

    const placement = live ? { x: live.x, y: live.y } : settled;

    return {
        placement,
        guides: live || zooming || hovering
            ? { travel: live?.travel ?? null, placement, editing: Boolean(live || zooming) }
            : null,
        handlers: onPlace ? {
            ref: pageRef,
            onPointerEnter: (event) => event.pointerType === 'mouse' && setHovering(true),
            onPointerLeave: () => setHovering(false),
            onPointerDown,
            onPointerMove,
            onPointerUp: (event) => finish(event, true),
            onPointerCancel: (event) => finish(event, false),
            onClickCapture,
            style: { touchAction: 'none', cursor: live ? 'grabbing' : 'grab' },
        } : { ref: pageRef, style: undefined },
    };
}

const percentOf = (value, fallback = 50) => {
    const number = parseFloat(value);
    return String(value).trim().endsWith('%') && Number.isFinite(number) ? number : fallback;
};

const backgroundSizes = new Map();

/** A painted background's own size, once the browser has it (it is already decoded). */
function backgroundNatural(src) {
    let probe = backgroundSizes.get(src);
    if (!probe) {
        probe = new Image();
        probe.src = src;
        backgroundSizes.set(src, probe);
    }
    return probe.naturalWidth ? { width: probe.naturalWidth, height: probe.naturalHeight } : null;
}

/**
 * The page's sharpest painted picture, which the faded copy draws from: an
 * <img>, or the top background layer the slideshow paints with.
 */
function paintedPicture(page) {
    let best = null;
    page.querySelectorAll('img').forEach((img) => {
        if (img.complete && img.naturalWidth && (!best || img.naturalWidth > best.el.naturalWidth)) {
            const style = getComputedStyle(img);
            best = {
                el: img,
                src: img.currentSrc || img.src,
                natural: { width: img.naturalWidth, height: img.naturalHeight },
                fit: style.objectFit,
                position: style.objectPosition,
            };
        }
    });
    if (best) return best;
    const layers = [...page.querySelectorAll('div')].filter((el) => el.style.backgroundImage.startsWith('url('));
    const top = layers[layers.length - 1];
    if (!top) return null;
    const src = top.style.backgroundImage.slice(4, -1).replace(/^["']|["']$/g, '').replace(/\\"/g, '"');
    const natural = backgroundNatural(src);
    if (!natural) return null;
    const style = getComputedStyle(top);
    return { el: top, src, natural, fit: style.backgroundSize, position: style.backgroundPosition };
}

/**
 * While a picture is placed, everything outside its sheet of paper (the
 * slideshow's spread) is greyed out, and the whole picture shows faded beyond
 * its page, so the parts the page crops off stay visible. Both live in <body>
 * because the page and the sheet clip their content; the page's own area is
 * cut out of the faded picture and keeps the real one.
 */
function PlacementOverflow({ editing }) {
    const anchorRef = useRef(null);
    const ghostRef = useRef(null);
    const veilRef = useRef(null);
    const editingRef = useRef(editing);
    editingRef.current = editing;
    const [doc, setDoc] = useState(null);

    useLayoutEffect(() => {
        setDoc(anchorRef.current?.ownerDocument || null);
    }, []);

    useLayoutEffect(() => {
        const page = anchorRef.current?.parentElement?.parentElement;
        const ghost = ghostRef.current;
        const veil = veilRef.current;
        if (!page || !ghost || !veil) return undefined;
        const sheet = page.closest('.spread-container');
        let frame = 0;
        const draw = () => {
            frame = requestAnimationFrame(draw);
            if (sheet) {
                const rect = sheet.getBoundingClientRect();
                veil.style.left = `${rect.left}px`;
                veil.style.top = `${rect.top}px`;
                veil.style.width = `${rect.width}px`;
                veil.style.height = `${rect.height}px`;
                veil.style.opacity = '1';
            }
            const picture = editingRef.current ? paintedPicture(page) : null;
            if (!picture) {
                ghost.style.display = 'none';
                return;
            }
            const [posX = '50%', posY = '50%'] = picture.position.split(/\s+/);
            const pic = pictureRect(
                picture.el.getBoundingClientRect(),
                picture.natural,
                picture.fit === 'cover' ? 'cover' : 'contain',
                { x: percentOf(posX), y: percentOf(posY) },
            );
            const own = page.getBoundingClientRect();
            const x1 = own.left - pic.left;
            const y1 = own.top - pic.top;
            const x2 = x1 + own.width;
            const y2 = y1 + own.height;
            const { src } = picture;
            ghost.style.display = 'block';
            ghost.style.left = `${pic.left}px`;
            ghost.style.top = `${pic.top}px`;
            ghost.style.width = `${pic.width}px`;
            ghost.style.height = `${pic.height}px`;
            if (ghost.dataset.src !== src) {
                ghost.dataset.src = src;
                ghost.style.backgroundImage = `url("${src.replace(/"/g, '\\"')}")`;
            }
            ghost.style.clipPath = `path(evenodd, "M0 0H${pic.width}V${pic.height}H0Z M${x1} ${y1}H${x2}V${y2}H${x1}Z")`;
        };
        draw();
        return () => cancelAnimationFrame(frame);
    }, [doc]);

    return (
        <>
            <span ref={anchorRef} hidden />
            {doc && createPortal(
                <>
                    <div
                        ref={veilRef}
                        aria-hidden="true"
                        className="pointer-events-none fixed z-[2147483000] opacity-0 transition-opacity duration-150"
                        style={{ boxShadow: '0 0 0 200vmax rgb(115 115 115 / 0.55)' }}
                    />
                    <div
                        ref={ghostRef}
                        aria-hidden="true"
                        className="pointer-events-none fixed z-[2147483000] bg-no-repeat opacity-40 outline-dashed outline-1 -outline-offset-1 outline-purple-500"
                        style={{ display: 'none', backgroundSize: '100% 100%' }}
                    />
                </>,
                doc.body,
            )}
        </>
    );
}

/**
 * While the image is placed: snap points for its centre during a drag, drawn
 * in page coordinates so they line up with where it will land, and the faded
 * picture beyond the page during a drag or zoom.
 */
export function PlacementGuides({ guides }) {
    if (!guides) return null;
    const { travel, placement } = guides;
    const steps = [];
    for (let v = 0; v <= 100; v += POSITION_STEP) steps.push(v);
    const axis = (room, size) => (Math.abs(room) >= 1 ? steps : [50]).map((v) => ({
        v,
        at: (((size - room) / 2 + (v / 100) * room) / size) * 100,
    }));
    const xs = travel ? axis(travel.x, travel.width) : [];
    const ys = travel ? axis(travel.y, travel.height) : [];
    return (
        <div className="pointer-events-none absolute inset-0 z-10" aria-hidden="true">
            <PlacementOverflow editing={Boolean(guides.editing)} />
            {ys.map((row) => xs.map((col) => {
                const active = col.v === placement.x && row.v === placement.y;
                return (
                    <span
                        key={`${col.v}-${row.v}`}
                        className={`absolute rounded-full -translate-x-1/2 -translate-y-1/2 ${active ? 'h-2.5 w-2.5 bg-purple-600 ring-2 ring-white' : 'h-1 w-1 bg-purple-500/70'}`}
                        style={{ left: `${col.at}%`, top: `${row.at}%` }}
                    />
                );
            }))}
        </div>
    );
}

PlacementOverflow.propTypes = {
    editing: PropTypes.bool,
};

PlacementGuides.propTypes = {
    guides: PropTypes.shape({
        travel: PropTypes.object,
        placement: PropTypes.object.isRequired,
        editing: PropTypes.bool,
    }),
};
